import React, { useState, useEffect, useRef } from 'react';
import { TextareaAutosize as BaseTextareaAutosize } from '@mui/base/TextareaAutosize';
import { styled } from '@mui/material/styles';
import './custom-input-textarea.scss';

interface InputTextAreaProps {
  title?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  value?: string | any;
  minRows?: number;
  maxRows?: number;
  ref?: any;
  onBlur?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
  ) => any;
  focusInput?: boolean;
  onFocus?: (
    e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>
  ) => any;
  inputTextAreaStyle?: React.CSSProperties;
  containerStyles?: React.CSSProperties;
  inputFieldProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;

  // New props for adornments
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  adornmentPosition?: 'top' | 'center' | 'bottom'; // Vertical alignment
}

const TextareaContainer = styled('div')`
  position: relative;
  display: flex;
  align-items: flex-start;
  border: 1px solid #d9d9d9;
  border-radius: 20px;
  background: #fff;

  &:hover {
    border-color: #000000;
  }

  &:focus-within {
    border-color: #33a7e8;
  }
`;

const Textarea = styled(BaseTextareaAutosize)`
  box-sizing: border-box;
  width: 100%;
  font-size: 14px;
  padding: 6px;
  margin: 2px;
  color: #000000;
  background: transparent;
  border: none;
  resize: none;
  outline: none;

  &::placeholder {
    color: #bfc0c2;
    opacity: 1;
    font-size: 14px;
    font-family: Segoe-UI-VF;
    font-weight: 400;
  }
`;

const AdornmentWrapper = styled('div')<{
  position: 'top' | 'center' | 'bottom';
}>`
  display: flex;
  align-self: ${(props) =>
    props.position === 'top'
      ? 'flex-start'
      : props.position === 'center'
        ? 'center'
        : 'flex-end'};
  padding: 8px;
  flex-shrink: 0;
`;

export const CustomInputTextArea = ({
  title,
  placeholder = '',
  value,
  minRows,
  maxRows,
  ref,
  onBlur,
  focusInput = false,
  required = false,
  disabled = false,
  onFocus,
  inputTextAreaStyle,
  containerStyles,
  inputFieldProps,
  onChange = () => {},
  startAdornment,
  endAdornment,
  adornmentPosition = 'center',
}: InputTextAreaProps) => {
  if (!ref) {
    ref = useRef<HTMLInputElement>(null);
  }
  const [val, setVal] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (focusInput) {
      ref?.current?.focus();
    } else {
      ref?.current?.blur();
    }
  }, [focusInput]);

  useEffect(() => {
    setVal(value);
  }, [value]);

  return (
    <div
      className="input_text_area tertiary-font-regular"
      style={containerStyles}
    >
      {title && (
        <span
          className={`input_text_area__title ${isFocused ? 'input_text_area__focused' : ''}`}
        >
          {title}
          {required && <span className="input_text__asterisk">*</span>}
        </span>
      )}

      <TextareaContainer>
        {startAdornment && (
          <AdornmentWrapper position={adornmentPosition}>
            {startAdornment}
          </AdornmentWrapper>
        )}

        <Textarea
          className="font-regular"
          style={inputTextAreaStyle}
          value={val}
          required={required}
          disabled={disabled}
          minRows={minRows}
          maxRows={maxRows}
          onFocus={(e) => {
            setIsFocused(true);
            onFocus && onFocus(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            onBlur && onBlur(e);
          }}
          onChange={(e) => onChange(e)}
          placeholder={placeholder}
          {...inputFieldProps}
        />

        {endAdornment && (
          <AdornmentWrapper position={adornmentPosition}>
            {endAdornment}
          </AdornmentWrapper>
        )}
      </TextareaContainer>
    </div>
  );
};
